import { useEffect, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { X, Plug, Lightbulb, Paperclip, Trash2, Link2, KeyRound, MessageSquare, FileText, Boxes } from 'lucide-react';
import { overlayDismiss } from '../../../lib/overlayDismiss';

/**
 * ApiIntegrationModal — API / MCP 串接導入
 *
 *   來源 Tab：
 *     [API 文件]  一次給文件 + 網址 + 認證 + 補充 → AI 拆解、編排多 skill（parse-api-docs /
 *                 add-api-nodes / add-form-collection / refine-agent-prompt / build-kb-from-files）
 *     [MCP Server] 給一台 MCP server（會自我描述工具）→ AI 用 add-mcp-nodes skill 連線探索工具
 *                  → 分組成 topic → 安全 append（既有節點全保留）。通話時走後端 MCP proxy。
 *
 *   兩種來源都是「組 prompt → 丟給 Claude Code agent 跑對應 skill」，下游編排由 AI 判斷。
 */

export function ApiIntegrationModal({ open, onClose, onApply, isStreaming, avatarName }) {
    const { t } = useTranslation('wsApi');
    const [mode, setMode] = useState('api'); // 'api' | 'mcp'

    // ── API 文件來源 ──
    const [files, setFiles] = useState([]);
    const [urls, setUrls] = useState('');
    const [creds, setCreds] = useState('');
    const [notes, setNotes] = useState('');
    const fileInputRef = useRef(null);

    // ── MCP Server 來源 ──
    const [mcpUrl, setMcpUrl] = useState('');
    const [mcpServerName, setMcpServerName] = useState('');
    const [mcpTransport, setMcpTransport] = useState('auto');
    const [mcpAuth, setMcpAuth] = useState('');
    const [mcpPrefix, setMcpPrefix] = useState('');
    const [mcpNotes, setMcpNotes] = useState('');

    useEffect(() => {
        if (!open) {
            setMode('api');
            setFiles([]); setUrls(''); setCreds(''); setNotes('');
            setMcpUrl(''); setMcpServerName(''); setMcpTransport('auto'); setMcpAuth(''); setMcpPrefix(''); setMcpNotes('');
        }
    }, [open]);

    if (!open) return null;

    const addFiles = (incoming) => {
        if (!incoming?.length) return;
        const next = [...files];
        for (const f of incoming) {
            if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
        }
        setFiles(next);
    };
    const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

    const canApplyApi = files.length > 0 || urls.trim() || creds.trim() || notes.trim();
    const canApplyMcp = mcpUrl.trim();
    const canApply = mode === 'api' ? canApplyApi : canApplyMcp;

    const handleApplyApi = () => {
        const fileLines = files.map((f, i) => `  ${i + 1}. ${f.name} (${Math.round(f.size / 1024)} KB)`).join('\n');
        const lines = [
            `請依下面提供的材料更新這個客服 (${avatarName || 'avatar'})。`,
            '',
            '這份內容可能包含**完整客服情境**（流程設計 / API 串接 / 認證 / 收單 / 應答規則 / KB 內容）。',
            '自行判斷涵蓋哪些部分，用對應 skill 一個一個套上去 — **不只做指令潤飾**。',
            '',
        ];
        if (files.length) {
            lines.push('【附上的文件】');
            lines.push(fileLines);
            lines.push('（路徑由 update endpoint 自動帶入。docx 用 pandoc 轉文字；pdf / 圖片用 Read tool vision。）');
            lines.push('');
        }
        if (urls.trim()) {
            lines.push('【相關網址】');
            lines.push(urls.trim());
            lines.push('（Swagger / OpenAPI / 系統文件 — 用 WebFetch 或 parse-api-docs 讀。）');
            lines.push('');
        }
        if (creds.trim()) {
            lines.push('【認證資訊】');
            lines.push('```');
            lines.push(creds.trim());
            lines.push('```');
            lines.push('（系統後端安全保存，最終回應**禁止**貼具體 token / 密碼值。）');
            lines.push('');
        }
        if (notes.trim()) {
            lines.push('【補充說明】');
            lines.push(notes.trim());
            lines.push('');
        }
        lines.push('## 執行步驟');
        lines.push('1. **拆解材料** — 在 thinking 階段列出材料涵蓋的部分：');
        lines.push('   - 流程結構（router scenarios / 多步對話 / 分支邏輯）?');
        lines.push('   - API 整合（端點 / auth / 回寫參數）?');
        lines.push('   - 收單欄位?');
        lines.push('   - 應答風格 / 限制 / few-shot?');
        lines.push('   - 知識庫內容?');
        lines.push('2. **挑對應 skill 依序套用**（這是「既有 avatar 增量更新」，務必走安全增量 skill，不要用 build_flow.js 整份重生 — 會洗掉既有知識庫節點）：');
        lines.push('   - **加 API 整合（apiTool 節點）→ add-api-nodes**（讀既有 flow → append，既有 KB 全保留；文件很亂 / 大時它會自己先 compose parse-api-docs 標準化，你不用另外呼叫）');
        lines.push('   - 收單需求 → **add-form-collection**');
        lines.push('   - 會員辨識（進線）→ **add-member-form-inbound**');
        lines.push('   - 應答風格 / few-shot → **refine-agent-prompt** 透過 **sync-ivr-config** 寫回');
        lines.push('   - 純 KB 內容 → **build-kb-from-files**（continued update）');
        lines.push('   - ⚠️ **只有從 0 建全新 avatar 才用 build-ivr-avatar**；對既有 avatar 一律用上面的增量 skill。');
        lines.push('3. 每步完成用**商家口吻**簡述做了什麼（B 端用戶看的，不要貼 JSON / 技術名詞）。');
        lines.push('4. ⚠️ 認證資訊絕對不要在回應裡貼具體 token / 密碼 — 講「Bearer Token 已設定」「帳密已記錄」即可。');

        const dParts = [];
        if (files.length) dParts.push(t('api.dpart.files', { n: files.length }));
        if (urls.trim()) dParts.push(t('api.dpart.urls', { n: urls.trim().split('\n').filter(Boolean).length }));
        if (creds.trim()) dParts.push(t('api.dpart.creds'));
        if (notes.trim()) dParts.push(t('api.dpart.notes'));
        const displayText = t('api.displayText', { parts: dParts.join(' + ') });

        onApply(lines.join('\n'), { files, displayText });
        onClose();
    };

    const handleApplyMcp = () => {
        const lines = [
            `請把下面這台 MCP server 串接進這個客服 (${avatarName || 'avatar'})。`,
            '',
            '這是 **MCP server**（Model Context Protocol — 會自我描述工具的伺服器）。',
            '用 **add-mcp-nodes** skill 處理（不是 add-api-nodes）。',
            '',
            '【MCP Server 網址】',
            mcpUrl.trim(),
            `【傳輸方式】 ${mcpTransport}`,
        ];
        if (mcpServerName.trim()) lines.push(`【Server 名稱】 ${mcpServerName.trim()}`);
        if (mcpPrefix.trim()) lines.push(`【工具名前綴】 ${mcpPrefix.trim()}`);
        if (mcpAuth.trim()) {
            lines.push('【認證資訊】');
            lines.push('```');
            lines.push(mcpAuth.trim());
            lines.push('```');
            lines.push('（系統後端安全保存，最終回應**禁止**貼具體 token / 密碼值。）');
        }
        if (mcpNotes.trim()) {
            lines.push('【補充說明】');
            lines.push(mcpNotes.trim());
        }
        lines.push('');
        lines.push('## 執行步驟（走 add-mcp-nodes skill）');
        lines.push('1. **讀既有 flow**（read-config）確認不會破壞既有節點。');
        lines.push('2. **連 MCP server 探索工具**（mcp-discover.mjs）— server 會自我描述工具，**不要猜**；連不上就停下來跟用戶確認網址 / 認證。');
        lines.push('3. **依工具語意分組成 topic**：工具少就一個 topic 全包；主題分明可分多個。想好 topicName / keywords（用戶會講的詞，router 靠這個路由）。');
        lines.push('4. 跑 **add_mcp_node.js** 安全 append（會 upsert mcpServers + 每工具生指向 MCP proxy 的 apiTool 節點 + router scenario + edge；既有 KB / 節點全保留）。');
        lines.push('5. **few-shot 一致性檢查**：新 topic 提到的 tool 名要跟實際 apis[].name 一致。');
        lines.push('6. 完成用**商家口吻**簡述串了哪些工具能力（不要貼 JSON / 技術名詞）。');
        lines.push('7. ⚠️ 認證資訊絕對不要在回應裡貼具體 token / 密碼 — 講「金鑰已設定」即可。');

        const displayText = t('mcp.displayText', { url: mcpServerName.trim() || mcpUrl.trim() });
        onApply(lines.join('\n'), { files: [], displayText });
        onClose();
    };

    const handleApply = () => {
        if (isStreaming || !canApply) return;
        if (mode === 'api') handleApplyApi();
        else handleApplyMcp();
    };

    const TabBtn = ({ id, icon: Icon, label }) => (
        <button
            onClick={() => setMode(id)}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-md transition border ${
                mode === id
                    ? 'bg-ink-900 text-white border-ink-900'
                    : 'bg-white text-ink-600 border-cream-300 hover:border-accent-400 hover:bg-cream-50'
            }`}
        >
            <Icon size={13} /> {label}
        </button>
    );

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400 inline-flex items-center gap-1.5">
                            <Plug size={11} /> {mode === 'api' ? 'api integration' : 'mcp integration'}
                        </div>
                        <h2 className="heading-display text-lg font-semibold text-ink-900 mt-0.5">
                            {t('combinedTitle')}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Source toggle */}
                <div className="px-6 pt-4">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-1.5">{t('src.label')}</div>
                    <div className="flex gap-2">
                        <TabBtn id="api" icon={FileText} label={t('src.tabApi')} />
                        <TabBtn id="mcp" icon={Boxes} label={t('src.tabMcp')} />
                    </div>
                </div>

                {/* Body */}
                <div className="px-6 py-5 flex-1 overflow-y-auto space-y-4">
                    {mode === 'api' ? (
                        <>
                            <div className="rounded-md bg-cream-100/60 border border-cream-200 px-3 py-2 text-xs text-ink-500 inline-flex items-start gap-2">
                                <Lightbulb size={14} strokeWidth={1.6} className="text-accent-700 shrink-0 mt-0.5" />
                                <div className="leading-relaxed">
                                    <Trans i18nKey="api.intro" t={t}>
                                        一次給<strong className="text-ink-700">完整情境</strong>（情境文件 + API 文件 + 認證 + 補充）—
                                        AI 會自動拆解、編排多個 skill 把整套（流程 / API / 收單 / 應答）套上去。
                                    </Trans>
                                    <br />
                                    <span className="text-ink-400 text-[11px]">{t('api.introHint')}</span>
                                </div>
                            </div>

                            {/* Files */}
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2 inline-flex items-center gap-1.5">
                                    <Paperclip size={11} /> {t('api.filesLabel')}
                                </div>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="w-full px-3 py-2.5 text-sm border border-dashed border-cream-300 hover:border-accent-400 hover:bg-cream-50 rounded-md bg-white text-ink-600 transition text-left"
                                >
                                    {files.length === 0 ? t('api.filePickEmpty') : t('api.filePickMore', { n: files.length })}
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
                                    accept=".pdf,.docx,.doc,.txt,.md,.png,.jpg,.jpeg,.webp,.pptx,.xlsx,.csv,.html,.json,.yaml,.yml"
                                    className="hidden"
                                />
                                {files.length > 0 && (
                                    <ul className="mt-2 space-y-1">
                                        {files.map((f, i) => (
                                            <li key={i} className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white border border-cream-200 rounded text-xs">
                                                <span className="truncate text-ink-700">
                                                    {f.name} <span className="text-ink-400 font-mono">({Math.round(f.size / 1024)} KB)</span>
                                                </span>
                                                <button onClick={() => removeFile(i)} className="size-5 inline-flex items-center justify-center text-ink-400 hover:text-danger-500" title={t('common:actions.delete')}>
                                                    <Trash2 size={11} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* URLs */}
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2 inline-flex items-center gap-1.5">
                                    <Link2 size={11} /> {t('api.urlsLabel')}
                                </div>
                                <textarea
                                    value={urls}
                                    onChange={(e) => setUrls(e.target.value)}
                                    rows={3}
                                    placeholder={t('api.urlsPlaceholder')}
                                    className="w-full resize-none px-3 py-2 text-xs border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-mono"
                                />
                            </div>

                            {/* Creds */}
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2 inline-flex items-center gap-1.5">
                                    <KeyRound size={11} /> {t('api.credsLabel')}
                                </div>
                                <textarea
                                    value={creds}
                                    onChange={(e) => setCreds(e.target.value)}
                                    rows={5}
                                    placeholder={t('api.credsPlaceholder')}
                                    className="w-full resize-none px-3 py-2 text-xs border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-mono"
                                />
                                <div className="text-[10.5px] text-ink-400 mt-1.5">{t('api.credsHint')}</div>
                            </div>

                            {/* Notes */}
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2 inline-flex items-center gap-1.5">
                                    <MessageSquare size={11} /> {t('api.notesLabel')}
                                </div>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={4}
                                    placeholder={t('api.notesPlaceholder')}
                                    className="w-full resize-none px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white leading-relaxed"
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="rounded-md bg-cream-100/60 border border-cream-200 px-3 py-2 text-xs text-ink-500 inline-flex items-start gap-2">
                                <Lightbulb size={14} strokeWidth={1.6} className="text-accent-700 shrink-0 mt-0.5" />
                                <div className="leading-relaxed">
                                    <Trans i18nKey="mcp.intro" t={t}>
                                        貼一台 <strong className="text-ink-700">MCP server</strong> 的網址 — AI 會自動連線探索它提供的工具，
                                        分組成情境後串進這個客服。通話時由系統安全轉呼叫。
                                    </Trans>
                                    <br />
                                    <span className="text-ink-400 text-[11px]">{t('mcp.introHint')}</span>
                                </div>
                            </div>

                            {/* MCP URL */}
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2 inline-flex items-center gap-1.5">
                                    <Link2 size={11} /> {t('mcp.urlLabel')}
                                </div>
                                <input
                                    type="text"
                                    value={mcpUrl}
                                    onChange={(e) => setMcpUrl(e.target.value)}
                                    placeholder={t('mcp.urlPlaceholder')}
                                    className="w-full px-3 py-2 text-xs border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-mono"
                                />
                            </div>

                            {/* Server name + transport */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2 inline-flex items-center gap-1.5">
                                        <Boxes size={11} /> {t('mcp.serverNameLabel')}
                                    </div>
                                    <input
                                        type="text"
                                        value={mcpServerName}
                                        onChange={(e) => setMcpServerName(e.target.value)}
                                        placeholder={t('mcp.serverNamePlaceholder')}
                                        className="w-full px-3 py-2 text-xs border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white"
                                    />
                                </div>
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2">{t('mcp.transportLabel')}</div>
                                    <select
                                        value={mcpTransport}
                                        onChange={(e) => setMcpTransport(e.target.value)}
                                        className="w-full px-3 py-2 text-xs border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white"
                                    >
                                        <option value="auto">{t('mcp.transportAuto')}</option>
                                        <option value="http">{t('mcp.transportHttp')}</option>
                                        <option value="sse">{t('mcp.transportSse')}</option>
                                    </select>
                                </div>
                            </div>

                            {/* Prefix */}
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2">{t('mcp.prefixLabel')}</div>
                                <input
                                    type="text"
                                    value={mcpPrefix}
                                    onChange={(e) => setMcpPrefix(e.target.value)}
                                    placeholder={t('mcp.prefixPlaceholder')}
                                    className="w-full px-3 py-2 text-xs border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-mono"
                                />
                            </div>

                            {/* Auth */}
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2 inline-flex items-center gap-1.5">
                                    <KeyRound size={11} /> {t('mcp.authLabel')}
                                </div>
                                <textarea
                                    value={mcpAuth}
                                    onChange={(e) => setMcpAuth(e.target.value)}
                                    rows={3}
                                    placeholder={t('mcp.authPlaceholder')}
                                    className="w-full resize-none px-3 py-2 text-xs border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-mono"
                                />
                                <div className="text-[10.5px] text-ink-400 mt-1.5">{t('api.credsHint')}</div>
                            </div>

                            {/* Notes */}
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2 inline-flex items-center gap-1.5">
                                    <MessageSquare size={11} /> {t('mcp.notesLabel')}
                                </div>
                                <textarea
                                    value={mcpNotes}
                                    onChange={(e) => setMcpNotes(e.target.value)}
                                    rows={3}
                                    placeholder={t('mcp.notesPlaceholder')}
                                    className="w-full resize-none px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white leading-relaxed"
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-cream-200">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition"
                    >
                        {t('common:actions.cancel')}
                    </button>
                    <button
                        onClick={handleApply}
                        disabled={isStreaming || !canApply}
                        className="px-4 py-2 text-sm rounded-md bg-ink-900 text-white hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed"
                    >
                        {mode === 'api' ? t('api.apply') : t('mcp.apply')}
                    </button>
                </div>
            </div>
        </div>
    );
}
